import { useEffect, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/client.ts";

/**
 * 收银台的全部业务逻辑。
 *
 * 🔴 **手机版和网页版是两套版面，但只能有一份逻辑。**
 *    什么时候建收款单、拆单怎么续、整组付完了没有、轮到什么时候停 ——
 *    这些抄两份的话迟早走岔，而走岔的地方全是钱。
 */

export interface SubOrder {
  orderNo: string;
  payUrl: string;
  /**
   * 🔴 用带 Cents 的那个字段。不带 Cents 的 `uniqueAmount` 是**元**（= cents/100），
   *    只为显示方便存在 —— 当成分喂给 <Price> 会把 ¥199.90 显示成 ¥1.999。
   */
  uniqueAmountCents: number;
}

export type CheckoutResult =
  | { success: true; split?: false; orderNo: string; payUrl: string; wechatPayUrl?: string }
  | { success: true; split: true; groupId: string; splitTotal: number; subOrders: SubOrder[] }
  | { success: false; error: string };

/**
 * 线下收款时买家要看到的东西。
 *
 * 🔴 线上支付有收银台，线下什么都没有 —— 下完单页面上只剩一个"待付款"，
 *    钱给谁、怎么给、给多少买家一概不知道，只会去问客服或者干脆不买了。
 */
export interface PayInstruction {
  collectMode: "offline_direct" | "platform_gateway";
  payableAmount: number;
  payment: string;
  expireAt: string | null;
  payee: {
    orgId: number;
    name: string;
    contactName: string;
    contactPhone: string;
    isPlatform: boolean;
  };
}

export interface QueryResult {
  status: "pending" | "processing" | "paid" | "expired" | "cancelled";
  /** 🔴 拆单组是不是全付完了。只有 groupId 存在时才返回。 */
  groupAllPaid?: boolean;
  groupPaidCount?: number;
  groupTotal?: number;
}

/** ⚠️ 服务端已经确认过这个组正好对应当前这一单，前端不用再比。 */
interface ResumableGroup {
  groupId: string;
  remainAmountCents: number;
  paidCount: number;
  splitTotal: number;
}

/** 这笔收款算不算收完了：单笔看自己，拆单看整组。 */
function isSettled(d: QueryResult | undefined): boolean {
  if (!d) return false;
  if (d.groupTotal !== undefined) return d.groupAllPaid === true;
  return d.status === "paid";
}

export function usePay() {
  const search = useSearch({ from: "/pay" }) as { orderIds?: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const orderIds = (search.orderIds ?? "")
    .split(",")
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0);

  const [result, setResult] = useState<CheckoutResult | null>(null);

  /**
   * 先问清楚这一单**怎么付** —— 这个商城默认不走线上支付。
   *
   * ⚠️ 已经是「已付款」就停止轮询，否则页面会一直在后台打请求。
   */
  const instruction = useQuery({
    queryKey: ["pay-instruction", orderIds[0]],
    queryFn: () => api.get<PayInstruction>(`/orders/${orderIds[0]}/pay-instruction`),
    enabled: orderIds.length > 0,
    refetchInterval: (q) => (q.state.data && q.state.data.payment === "pending" ? 5000 : false),
  });
  const offline = instruction.data?.collectMode === "offline_direct";

  const create = useMutation({
    /**
     * 🔴 **先看有没有付了一半的拆单组，有就接着付。**
     *    直接新建的话，用户关掉页面再回来会开出**新的一组**，
     *    之前付进去的钱留在老组里 —— 老组永远凑不齐、新组要重新付一遍全款。
     *
     * ⚠️ 只在这一单的组上续。别人的单/别的单的组不能拿来顶。
     */
    mutationFn: async () => {
      const resumable = await api.get<ResumableGroup | null>(
        `/pay/resumable?orderIds=${orderIds.join(",")}`,
      );
      if (resumable) return api.post<CheckoutResult>(`/pay/resume/${resumable.groupId}`, {});
      return api.post<CheckoutResult>("/pay/checkout", { orderIds });
    },
    onSuccess: setResult,
  });

  /**
   * 进页面就建收款单。⚠️ 只建一次 —— 重复建会在网关那边留下一堆废单。
   *
   * 🔴 **要等模式问清楚再建。** 线下收款根本没有网关，
   *    抢跑去建收款单只会拿回一个报错，而用户看到的是"收银台打不开"。
   */
  useEffect(() => {
    if (!instruction.data || offline) return;
    if (orderIds.length > 0 && !result && !create.isPending && !create.error) create.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instruction.data, offline]);

  /**
   * 轮询。⚠️ 认单看的是 `paid` —— `processing` 表示钱到了但货还在发，
   *    这时候刷新页面用户会看到"付了钱什么都没有"。
   * 顺带一提，这个查询在服务端会向网关核对一次，能兜住丢失的回调。
   */
  const firstNo =
    result && result.success
      ? result.split
        ? result.subOrders[0]?.orderNo
        : result.orderNo
      : undefined;

  const poll = useQuery({
    queryKey: ["pay", firstNo],
    queryFn: () => api.get<QueryResult>(`/pay/orders/${firstNo}`),
    enabled: Boolean(firstNo),
    refetchInterval: (q) => (isSettled(q.state.data) ? false : 3000),
  });

  /**
   * 🔴 **拆单时不能看单笔的 status。**
   *    第一笔付完它就是 `paid`，但整单还欠着后面几笔 —— 照单笔判定的话
   *    用户付了 1/5 就被告知"支付成功"，货却永远发不出去。
   *    整组付完与否看 `groupAllPaid`。
   */
  const done = poll.data ? isSettled(poll.data) : false;

  useEffect(() => {
    if (done) {
      /** ⚠️ 先把订单缓存作废再跳，否则订单页拿的是"待付款"那份旧数据。 */
      void qc.invalidateQueries({ queryKey: ["orders"] });
      const t = setTimeout(() => navigate({ to: "/orders", search: {} }), 1500);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [done, navigate, qc]);

  return {
    orderIds,
    instruction,
    offline,
    create,
    result,
    poll,
    done,
    paidCount: poll.data?.groupPaidCount ?? 0,
    goOrders: () => navigate({ to: "/orders", search: {} }),
  };
}
